import type { QueryClient } from "@tanstack/react-query"

/**
 * Invalida todas as queries relacionadas a participação em um jogo.
 *
 * Chamar sempre que houver alteração em game participant:
 * - Inscrição: registerParticipant
 * - Cancelamento: cancelParticipant, cancelParticipantByAdmin
 * - Status de pagamento: confirmPayment, refundTransaction
 * - Status do usuário no jogo: checkIn
 */
export function invalidateGameParticipantQueries(
  queryClient: QueryClient,
  gameId: string,
): void {
  // Queries específicas do jogo
  queryClient.invalidateQueries({ queryKey: ["gameDetails", gameId] })
  queryClient.invalidateQueries({ queryKey: ["gameParticipants", gameId] })
  queryClient.invalidateQueries({ queryKey: ["gameParticipantsDetails", gameId] })
  queryClient.invalidateQueries({ queryKey: ["game", gameId] })
  queryClient.invalidateQueries({ queryKey: ["gameRegistrationSuccess", gameId] })
  queryClient.invalidateQueries({ queryKey: ["gameCancel", gameId] })
  queryClient.invalidateQueries({ queryKey: ["gameRegistrationSuccess", gameId] })

  // Status de participante (afeta múltiplos jogos)
  queryClient.invalidateQueries({
    predicate: (query) => query.queryKey[0] === "participantStatus",
  })

  // Team dashboard e team games (podem listar o jogo)
  queryClient.invalidateQueries({
    predicate: (query) =>
      query.queryKey[0] === "teamDashboard" || query.queryKey[0] === "teamGames",
  })

  // Team details (stats com contagem de jogos)
  queryClient.invalidateQueries({
    predicate: (query) => query.queryKey[0] === "team",
  })

  // Meus jogos / participações (MyGamesPage usa "myGames")
  queryClient.invalidateQueries({
    predicate: (query) =>
      query.queryKey[0] === "myParticipations" ||
      query.queryKey[0] === "myParticipationsEnriched" ||
      query.queryKey[0] === "myGames",
  })

  // Jogos públicos (HomePage) – atualiza contagem de participantes nos cards
  queryClient.invalidateQueries({
    predicate: (query) => query.queryKey[0] === "publicGames",
  })

  // Refetch imediato da tela de detalhes para atualizar assim que o usuário voltar
  queryClient.refetchQueries({ queryKey: ["gameDetails", gameId] })

  // Sorteio de equipes (posição pretendida dos participantes)
  queryClient.invalidateQueries({
    predicate: (query) =>
      (query.queryKey[0] === "persistedGameSquads" ||
        query.queryKey[0] === "teamSquadRatingInsights") &&
      query.queryKey[2] === gameId,
  })
}

type TrainingEnrollmentInvalidation = {
  kind?: "event" | "class";
  sessionId?: string;
  teamRef?: string;
  classId?: string;
};

/**
 * Invalida queries após inscrição, pagamento ou cancelamento em eventos/CT.
 */
export function invalidateTrainingEnrollmentQueries(
  queryClient: QueryClient,
  options?: TrainingEnrollmentInvalidation,
): void {
  queryClient.invalidateQueries({
    predicate: (query) =>
      query.queryKey[0] === "myEventEnrollments" ||
      query.queryKey[0] === "myClassEnrollments",
  });

  if (options?.sessionId) {
    queryClient.invalidateQueries({
      queryKey: ["trainingSessionPublic", options.sessionId],
    });
  } else {
    queryClient.invalidateQueries({
      predicate: (query) => query.queryKey[0] === "trainingSessionPublic",
    });
  }

  if (options?.teamRef && options?.classId) {
    queryClient.invalidateQueries({
      queryKey: ["trainingClassPublic", options.teamRef, options.classId],
    });
  } else {
    queryClient.invalidateQueries({
      predicate: (query) => query.queryKey[0] === "trainingClassPublic",
    });
  }

  queryClient.invalidateQueries({
    predicate: (query) =>
      query.queryKey[0] === "publicTrainingEvents" ||
      query.queryKey[0] === "publicTrainingClasses" ||
      query.queryKey[0] === "trainingEnrollmentSuccess",
  });

  if (!options?.kind || options.kind === "event") {
    void queryClient.refetchQueries({
      predicate: (query) => query.queryKey[0] === "myEventEnrollments",
    });
  }
  if (!options?.kind || options.kind === "class") {
    void queryClient.refetchQueries({
      predicate: (query) => query.queryKey[0] === "myClassEnrollments",
    });
  }
}

/**
 * Invalida queries após inscrição ou pagamento de torneio.
 */
export function invalidateTournamentRegistrationQueries(
  queryClient: QueryClient,
  tournamentId?: string,
): void {
  queryClient.invalidateQueries({
    predicate: (query) => query.queryKey[0] === "myTournamentRegistrations",
  });

  if (tournamentId) {
    queryClient.invalidateQueries({
      predicate: (query) =>
        query.queryKey[0] === "tournament" &&
        (query.queryKey[1] === tournamentId || query.queryKey[2] === tournamentId),
    });
  }

  queryClient.invalidateQueries({
    predicate: (query) => query.queryKey[0] === "publicTournaments",
  });

  void queryClient.refetchQueries({
    predicate: (query) => query.queryKey[0] === "myTournamentRegistrations",
  });
}
